import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  LogOut, Download, Plus, Trash2, Edit3, RefreshCw, BarChart3,
  Users, Calendar, ExternalLink, Save, X, Code2, Copy as CopyIcon,
  CheckCircle2, AlertCircle, ChevronDown, Wand2, ArrowRight,
  Image as ImageIcon, Upload,
} from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "../context/AuthContext";
import { api, formatApiError, API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import AdminEmbedPanel from "./AdminEmbedPanel";
import AdminShareCardPanel from "./AdminShareCardPanel";

const LOGO_URL =
  "https://www.ontour.scootive.pl/img/logo.svg";

function StatCard({ label, value, accent }) {
  return (
    <div
      className="surface-soft p-5"
      style={
        accent
          ? {
              background:
                "linear-gradient(135deg, rgba(46,108,46,0.12) 0%, rgba(46,108,46,0.04) 100%)",
            }
          : undefined
      }
    >
      <p className="text-[10px] font-medium uppercase tracking-[0.3em] text-black/45 mb-2">
        {label}
      </p>
      <p
        className={`font-display text-4xl tabular-nums leading-none ${accent ? "text-brand-green" : "text-black"}`}
      >
        {value ?? 0}
      </p>
    </div>
  );
}

function CampaignPicker({ campaigns, value, onChange, label = "Kampania" }) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="text-[10px] font-medium uppercase tracking-[0.25em] text-black/45">
        {label}
      </span>
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger
          data-testid="campaign-picker"
          className="h-9 min-w-[260px] bg-white border-black/10 input-rounded shadow-sm text-sm"
        >
          <SelectValue placeholder="Wybierz kampanię" />
        </SelectTrigger>
        <SelectContent className="bg-white border-black/10 input-rounded">
          {campaigns.map((c) => (
            <SelectItem key={c.id} value={c.id} className="text-sm">
              <span className="flex items-center gap-2">
                {c.is_active && (
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-brand-green" />
                )}
                {c.title}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const [stats, setStats] = useState(null);
  const [skateparks, setSkateparks] = useState([]);

  const [spDialog, setSpDialog] = useState({ open: false, editing: null });
  const [campDialog, setCampDialog] = useState({ open: false, editing: null });
  const [wizardOpen, setWizardOpen] = useState(false);
  const [copyDialog, setCopyDialog] = useState({ open: false });
  const [importDialog, setImportDialog] = useState({ open: false });

  const activeCampaign = useMemo(
    () => campaigns.find((c) => c.is_active),
    [campaigns]
  );
  const selectedCampaign = useMemo(
    () => campaigns.find((c) => c.id === selectedCampaignId) || activeCampaign,
    [campaigns, selectedCampaignId, activeCampaign]
  );

  const reloadCampaigns = useCallback(async () => {
    try {
      const { data } = await api.get("/campaigns");
      setCampaigns(data);
      if (!selectedCampaignId) {
        const active = data.find((c) => c.is_active);
        setSelectedCampaignId(active?.id || data[0]?.id || null);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [selectedCampaignId]);

  const reloadForSelected = useCallback(async () => {
    if (!selectedCampaign) return;
    try {
      const [s, sp] = await Promise.all([
        api.get(`/admin/stats?campaign_id=${selectedCampaign.id}`),
        api.get(`/skateparks?campaign_id=${selectedCampaign.id}&active_only=false&limit=2000&include_codes=1`),
      ]);
      setStats(s.data);
      setSkateparks(sp.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [selectedCampaign]);

  useEffect(() => {
    reloadCampaigns();
  }, [reloadCampaigns]);

  useEffect(() => {
    reloadForSelected();
    const t = setInterval(reloadForSelected, 20000);
    return () => clearInterval(t);
  }, [reloadForSelected]);

  const onLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  const handleExport = async (opts = {}) => {
    try {
      const token = localStorage.getItem("scootive_admin_token");
      const qs = new URLSearchParams();
      if (opts.campaign_id) qs.set("campaign_id", opts.campaign_id);
      if (opts.scope) qs.set("scope", opts.scope);
      if (opts.from) qs.set("from", opts.from);
      if (opts.to) qs.set("to", opts.to);
      if (opts.format) qs.set("format", opts.format);
      const res = await fetch(`${API}/admin/votes/export?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t.slice(0, 120) || "Eksport nieudany");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ext = opts.format === "json" ? "json" : "csv";
      a.download = `scootive_${opts.scope || "votes"}_${Date.now()}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Wyeksportowano");
    } catch (e) {
      toast.error(e.message);
    }
  };

  const [exportOpen, setExportOpen] = useState(false);

  const leaderboard = stats?.leaderboard || [];

  return (
    <div
      className="min-h-screen text-black"
      style={{
        background:
          "radial-gradient(110% 60% at 50% -8%, rgba(46,108,46,0.10) 0%, rgba(46,108,46,0.03) 40%, #fafafa 70%)",
      }}
    >
      {/* Header */}
      <header className="border-b border-black/5 glass sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Scootive" className="h-8" />
            <span className="text-[10px] font-medium uppercase tracking-[0.3em] text-brand-green px-2.5 py-1 rounded-full bg-brand-green/8">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="text-[11px] font-medium uppercase tracking-wider text-black/55 hover:text-brand-green hidden sm:inline-flex items-center gap-1"
            >
              <ExternalLink className="w-3 h-3" /> Kampania
            </Link>
            <Link
              to="/historia"
              className="text-[11px] font-medium uppercase tracking-wider text-black/55 hover:text-brand-green hidden sm:inline-flex items-center gap-1"
            >
              <ExternalLink className="w-3 h-3" /> Archiwum
            </Link>
            <span className="text-[11px] text-black/40 hidden md:inline">{user?.email}</span>
            <Button
              data-testid="admin-logout-btn"
              onClick={onLogout}
              className="btn-soft h-9 px-3 gap-2 text-xs font-medium"
            >
              <LogOut className="w-3.5 h-3.5" /> Wyloguj
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-6">
        {/* Campaign picker bar */}
        <div className="surface-soft p-3 sm:p-4 mb-5 flex items-center justify-between gap-3 flex-wrap">
          <CampaignPicker
            campaigns={campaigns}
            value={selectedCampaignId}
            onChange={setSelectedCampaignId}
          />
          {selectedCampaign && (
            <div className="flex items-center gap-2 flex-wrap">
              {selectedCampaign.is_active ? (
                <span className="inline-flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.25em] text-brand-green px-2.5 py-1 rounded-full bg-brand-green/10">
                  <CheckCircle2 className="w-3 h-3" /> Aktywna
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.25em] text-black/50 px-2.5 py-1 rounded-full bg-black/5">
                  Zarchiwizowana
                </span>
              )}
              <Button
                data-testid="edit-selected-campaign-btn"
                onClick={() => setCampDialog({ open: true, editing: selectedCampaign })}
                className="btn-soft h-9 px-3 gap-2 text-xs font-medium"
              >
                <Edit3 className="w-3.5 h-3.5" /> Ustawienia kampanii
              </Button>
              <Button
                data-testid="open-wizard-btn"
                onClick={() => setWizardOpen(true)}
                className="btn-soft h-9 px-3 gap-2 text-xs font-medium"
              >
                <Wand2 className="w-3.5 h-3.5" /> Wizard
              </Button>
              <Button
                data-testid="add-campaign-btn-top"
                onClick={() => setCampDialog({ open: true, editing: null })}
                className="btn-brand h-9 px-3.5 gap-2 text-xs font-semibold"
              >
                <Plus className="w-3.5 h-3.5" /> Nowa kampania
              </Button>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <StatCard label="Łącznie głosów" value={stats?.total_votes} accent />
          <StatCard label="Unikalni wyborcy" value={stats?.unique_voters} />
          <StatCard label="Głosy dziś" value={stats?.votes_today} />
          <StatCard label="Opcje w kampanii" value={skateparks.length} />
        </div>

        <Tabs defaultValue="ranking" className="w-full">
          <TabsList className="bg-white shadow-sm rounded-full p-1 h-auto inline-flex">
            <TabsTrigger
              data-testid="tab-ranking"
              value="ranking"
              className="data-[state=active]:bg-brand-green data-[state=active]:text-white data-[state=active]:shadow-sm rounded-full font-medium uppercase tracking-wider px-4 py-2 text-xs"
            >
              <BarChart3 className="w-3.5 h-3.5 mr-2" /> Ranking
            </TabsTrigger>
            <TabsTrigger
              data-testid="tab-skateparks"
              value="skateparks"
              className="data-[state=active]:bg-brand-green data-[state=active]:text-white data-[state=active]:shadow-sm rounded-full font-medium uppercase tracking-wider px-4 py-2 text-xs"
            >
              <Users className="w-3.5 h-3.5 mr-2" /> Opcje głosowania
            </TabsTrigger>
            <TabsTrigger
              data-testid="tab-campaigns"
              value="campaigns"
              className="data-[state=active]:bg-brand-green data-[state=active]:text-white data-[state=active]:shadow-sm rounded-full font-medium uppercase tracking-wider px-4 py-2 text-xs"
            >
              <Calendar className="w-3.5 h-3.5 mr-2" /> Kampanie
            </TabsTrigger>
            <TabsTrigger
              data-testid="tab-embed"
              value="embed"
              className="data-[state=active]:bg-brand-green data-[state=active]:text-white data-[state=active]:shadow-sm rounded-full font-medium uppercase tracking-wider px-4 py-2 text-xs"
            >
              <Code2 className="w-3.5 h-3.5 mr-2" /> Embed
            </TabsTrigger>
            <TabsTrigger
              data-testid="tab-sharecard"
              value="sharecard"
              className="data-[state=active]:bg-brand-green data-[state=active]:text-white data-[state=active]:shadow-sm rounded-full font-medium uppercase tracking-wider px-4 py-2 text-xs"
            >
              <ImageIcon className="w-3.5 h-3.5 mr-2" /> Share Card
            </TabsTrigger>
          </TabsList>

          {/* RANKING */}
          <TabsContent value="ranking" className="mt-5">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div>
                <h2 className="font-display text-2xl uppercase tracking-tight">
                  Ranking · {selectedCampaign?.title || "—"}
                </h2>
                <p className="text-xs text-black/50 mt-0.5">
                  {leaderboard.length} pozycji · {stats?.total_votes || 0} głosów łącznie
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={reloadForSelected}
                  data-testid="reload-btn"
                  className="btn-soft h-9 px-3 gap-2 text-xs font-medium"
                >
                  <RefreshCw className="w-3.5 h-3.5" /> Odśwież
                </Button>
                <Button
                  onClick={() => setExportOpen(true)}
                  data-testid="export-csv-btn"
                  className="btn-brand h-9 px-3.5 gap-2 text-xs font-semibold"
                >
                  <Download className="w-3.5 h-3.5" /> Eksportuj
                </Button>
              </div>
            </div>
            <div className="surface-soft overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-black/5 hover:bg-transparent">
                    <TableHead className="text-black/45 font-medium uppercase text-[10px] tracking-[0.2em]">#</TableHead>
                    <TableHead className="text-black/45 font-medium uppercase text-[10px] tracking-[0.2em]">Miasto / opcja</TableHead>
                    <TableHead className="text-black/45 font-medium uppercase text-[10px] tracking-[0.2em]">Opis</TableHead>
                    <TableHead className="text-black/45 font-medium uppercase text-[10px] tracking-[0.2em] text-right">Głosy</TableHead>
                    <TableHead className="text-black/45 font-medium uppercase text-[10px] tracking-[0.2em] text-right">%</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leaderboard.length === 0 && (
                    <TableRow className="border-black/5">
                      <TableCell colSpan={5} className="text-center text-black/40 py-10 text-sm">
                        Brak głosów w tej kampanii
                      </TableCell>
                    </TableRow>
                  )}
                  {leaderboard.map((r, idx) => (
                    <TableRow key={r.skatepark_id} className="border-black/5 hover:bg-black/[0.02]">
                      <TableCell className="font-display text-xl text-black/40 tabular-nums">
                        {String(idx + 1).padStart(2, "0")}
                      </TableCell>
                      <TableCell className="font-semibold">{r.city}</TableCell>
                      <TableCell className="text-black/55">{r.skatepark}</TableCell>
                      <TableCell className="text-right font-display text-xl text-brand-green tabular-nums">
                        {r.votes}
                      </TableCell>
                      <TableCell className="text-right text-black/55 tabular-nums">{r.percentage}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* SKATEPARKS */}
          <TabsContent value="skateparks" className="mt-5">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div>
                <h2 className="font-display text-2xl uppercase tracking-tight">
                  Opcje głosowania · {selectedCampaign?.title || "—"}
                </h2>
                <p className="text-xs text-black/50 mt-0.5">
                  {skateparks.length} opcji w tej kampanii
                </p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  onClick={() => setImportDialog({ open: true })}
                  data-testid="import-csv-btn"
                  className="btn-soft h-9 px-3 gap-2 text-xs font-medium"
                >
                  <Upload className="w-3.5 h-3.5" /> Import CSV
                </Button>
                <Button
                  onClick={() => setCopyDialog({ open: true })}
                  data-testid="copy-options-btn"
                  className="btn-soft h-9 px-3 gap-2 text-xs font-medium"
                  disabled={campaigns.length < 2}
                >
                  <CopyIcon className="w-3.5 h-3.5" /> Kopiuj z innej kampanii
                </Button>
                <Button
                  onClick={() => setSpDialog({ open: true, editing: null })}
                  data-testid="add-skatepark-btn"
                  className="btn-brand h-9 px-3.5 gap-2 text-xs font-semibold"
                >
                  <Plus className="w-3.5 h-3.5" /> Dodaj opcję
                </Button>
              </div>
            </div>
            <div className="surface-soft overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-black/5 hover:bg-transparent">
                    <TableHead className="text-black/45 font-medium uppercase text-[10px] tracking-[0.2em]">Nazwa</TableHead>
                    <TableHead className="text-black/45 font-medium uppercase text-[10px] tracking-[0.2em]">Opis</TableHead>
                    <TableHead className="text-black/45 font-medium uppercase text-[10px] tracking-[0.2em]">Kody</TableHead>
                    <TableHead className="text-black/45 font-medium uppercase text-[10px] tracking-[0.2em]">Region</TableHead>
                    <TableHead className="text-black/45 font-medium uppercase text-[10px] tracking-[0.2em]">Aktywna</TableHead>
                    <TableHead className="text-black/45 font-medium uppercase text-[10px] tracking-[0.2em] text-right">Akcje</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {skateparks.length === 0 && (
                    <TableRow className="border-black/5">
                      <TableCell colSpan={6} className="text-center text-black/40 py-10 text-sm">
                        Brak opcji w tej kampanii. Dodaj pierwszą lub skopiuj z innej.
                      </TableCell>
                    </TableRow>
                  )}
                  {skateparks.map((sp) => (
                    <TableRow key={sp.id} className="border-black/5 hover:bg-black/[0.02]">
                      <TableCell className="font-semibold">{sp.city}</TableCell>
                      <TableCell className="text-black/65">{sp.skatepark}</TableCell>
                      <TableCell className="text-black/45 text-xs">
                        {(sp.postal_codes || []).join(", ")}
                      </TableCell>
                      <TableCell className="text-black/55 text-sm">{sp.region}</TableCell>
                      <TableCell>
                        <span
                          className={`text-[10px] font-medium uppercase tracking-wider px-2 py-1 rounded-full ${sp.active ? "bg-brand-green/10 text-brand-green" : "bg-black/5 text-black/45"}`}
                        >
                          {sp.active ? "Tak" : "Nie"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            data-testid={`edit-sp-${sp.id}`}
                            onClick={() => setSpDialog({ open: true, editing: sp })}
                            className="btn-soft h-8 w-8 p-0"
                          >
                            <Edit3 className="w-3 h-3" />
                          </Button>
                          <Button
                            data-testid={`delete-sp-${sp.id}`}
                            onClick={async () => {
                              if (!window.confirm(`Usunąć ${sp.city}?`)) return;
                              try {
                                await api.delete(`/skateparks/${sp.id}`);
                                toast.success("Usunięto");
                                reloadForSelected();
                              } catch (e) {
                                toast.error(formatApiError(e));
                              }
                            }}
                            className="btn-soft h-8 w-8 p-0 hover:!bg-red-50 hover:!text-red-600"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* CAMPAIGNS */}
          <TabsContent value="campaigns" className="mt-5">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h2 className="font-display text-2xl uppercase tracking-tight">Kampanie</h2>
              <div className="flex gap-2">
                <Button
                  data-testid="open-wizard-btn-2"
                  onClick={() => setWizardOpen(true)}
                  className="btn-soft h-9 px-3 gap-2 text-xs font-medium"
                >
                  <Wand2 className="w-3.5 h-3.5" /> Wizard
                </Button>
                <Button
                  onClick={() => setCampDialog({ open: true, editing: null })}
                  data-testid="add-campaign-btn"
                  className="btn-brand h-9 px-3.5 gap-2 text-xs font-semibold"
                >
                  <Plus className="w-3.5 h-3.5" /> Nowa kampania
                </Button>
              </div>
            </div>
            <div className="space-y-3">
              {campaigns.map((c) => (
                <div
                  key={c.id}
                  className={`surface-soft p-5 flex items-start justify-between gap-4 flex-wrap ${c.is_active ? "ring-2 ring-brand-green/50" : ""}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      {c.is_active ? (
                        <span className="text-[10px] font-medium uppercase tracking-wider px-2.5 py-1 rounded-full bg-brand-green text-white">
                          Aktywna
                        </span>
                      ) : (
                        <span className="text-[10px] font-medium uppercase tracking-wider px-2.5 py-1 rounded-full bg-black/5 text-black/55">
                          Zarchiwizowana
                        </span>
                      )}
                      <p className="font-display text-xl uppercase tracking-tight">{c.title}</p>
                    </div>
                    {c.subtitle && <p className="text-black/55 text-sm">{c.subtitle}</p>}
                    <div className="flex items-center gap-3 mt-2 flex-wrap text-[11px] text-black/45">
                      <span>Wybieracie: <strong className="text-black/65">{c.option_label}</strong></span>
                      <span>·</span>
                      <span>Wyświetlanie: <strong className="text-black/65">{c.display_mode === "radio" ? "Radio" : "Dropdown"}</strong></span>
                      <span>·</span>
                      <span>Ranking: <strong className="text-black/65">{c.results_title}</strong></span>
                      <span>·</span>
                      <span>
                        Limit:{" "}
                        <strong className={(c.vote_limit_mode || (c.vote_limit_enabled === false ? "off" : "days")) !== "off" ? "text-brand-green" : "text-black/65"}>
                          {(() => {
                            const mode = c.vote_limit_mode || (c.vote_limit_enabled === false ? "off" : "days");
                            if (mode === "off") return "BEZ LIMITU";
                            if (mode === "lifetime") return "RAZ NA ZAWSZE";
                            const d = Number(c.vote_limit_days) || 1;
                            return d === 1 ? "1×/DZIEŃ" : `1× / ${d} DNI`;
                          })()}
                        </strong>
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {!c.is_active && (
                      <Button
                        data-testid={`activate-campaign-${c.id}`}
                        onClick={async () => {
                          try {
                            await api.patch(`/campaigns/${c.id}`, { is_active: true });
                            toast.success("Aktywowano kampanię");
                            reloadCampaigns();
                            reloadForSelected();
                          } catch (e) {
                            toast.error(formatApiError(e));
                          }
                        }}
                        className="btn-brand h-9 px-3.5 gap-2 text-xs font-semibold"
                      >
                        Aktywuj
                      </Button>
                    )}
                    <Button
                      data-testid={`edit-campaign-${c.id}`}
                      onClick={() => setCampDialog({ open: true, editing: c })}
                      className="btn-soft h-9 px-3 gap-2 text-xs font-medium"
                    >
                      <Edit3 className="w-3 h-3" /> Edytuj
                    </Button>
                    <Button
                      data-testid={`delete-campaign-${c.id}`}
                      onClick={async () => {
                        if (!window.confirm("Usunąć kampanię? To także skasuje wszystkie głosy.")) return;
                        try {
                          await api.delete(`/campaigns/${c.id}`);
                          toast.success("Usunięto");
                          reloadCampaigns();
                        } catch (e) {
                          toast.error(formatApiError(e));
                        }
                      }}
                      className="btn-soft h-9 px-3 gap-2 text-xs font-medium hover:!bg-red-50 hover:!text-red-600"
                    >
                      <Trash2 className="w-3 h-3" /> Usuń
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* EMBED */}
          <TabsContent value="embed" className="mt-5">
            <AdminEmbedPanel />
          </TabsContent>
          <TabsContent value="sharecard" className="mt-5">
            <AdminShareCardPanel
              campaign={selectedCampaign}
              leaderboard={leaderboard}
              totalVotes={stats?.total_votes || 0}
            />
          </TabsContent>
        </Tabs>

        <SkateparkDialog
          state={spDialog}
          campaignId={selectedCampaign?.id}
          onClose={() => setSpDialog({ open: false, editing: null })}
          onSaved={reloadForSelected}
        />
        <CampaignDialog
          state={campDialog}
          onClose={() => setCampDialog({ open: false, editing: null })}
          onSaved={() => {
            reloadCampaigns();
            reloadForSelected();
          }}
        />
        <CopyOptionsDialog
          open={copyDialog.open}
          campaigns={campaigns}
          destCampaignId={selectedCampaign?.id}
          onClose={() => setCopyDialog({ open: false })}
          onCopied={reloadForSelected}
        />
        <ImportCsvDialog
          open={importDialog.open}
          campaignId={selectedCampaign?.id}
          onClose={() => setImportDialog({ open: false })}
          onImported={reloadForSelected}
        />
        <CampaignWizard
          open={wizardOpen}
          campaigns={campaigns}
          onClose={() => setWizardOpen(false)}
          onCreated={(newId) => {
            setSelectedCampaignId(newId);
            reloadCampaigns();
            reloadForSelected();
          }}
        />
        <ExportDialog
          open={exportOpen}
          onClose={() => setExportOpen(false)}
          campaigns={campaigns}
          defaultCampaignId={selectedCampaign?.id}
          onExport={handleExport}
        />
      </main>
    </div>
  );
}

function SkateparkDialog({ state, campaignId, onClose, onSaved }) {
  const editing = state.editing;
  const [form, setForm] = useState({
    city: "", skatepark: "", postal_codes: "", region: "", active: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (state.open) {
      setForm({
        city: editing?.city || "",
        skatepark: editing?.skatepark || "",
        postal_codes: (editing?.postal_codes || []).join(", "),
        region: editing?.region || "",
        active: editing?.active ?? true,
      });
    }
  }, [state.open, editing]);

  const onSave = async () => {
    if (!form.city.trim()) {
      toast.error("Nazwa główna jest wymagana");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        city: form.city.trim(),
        skatepark: form.skatepark.trim(),
        postal_codes: form.postal_codes.split(",").map((s) => s.trim()).filter(Boolean),
        region: form.region.trim() || null,
        active: form.active,
        campaign_id: campaignId,
      };
      if (editing) {
        await api.patch(`/skateparks/${editing.id}`, payload);
      } else {
        await api.post("/skateparks", payload);
      }
      toast.success("Zapisano");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={state.open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="surface-card border-0 text-black max-w-lg p-0 overflow-hidden">
        <div className="p-6">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl uppercase tracking-tight">
              {editing ? "Edytuj opcję" : "Nowa opcja głosowania"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            <FieldInput label="Nazwa główna (miasto / kraj / itp.)" value={form.city} onChange={(v) => setForm({ ...form, city: v })} testId="sp-city-input" />
            <FieldInput label="Opis / podtytuł (opcjonalny)" value={form.skatepark} onChange={(v) => setForm({ ...form, skatepark: v })} placeholder="np. nazwa skateparku" testId="sp-name-input" />
            <FieldInput label="Kody pocztowe (oddziel przecinkami)" value={form.postal_codes} onChange={(v) => setForm({ ...form, postal_codes: v })} placeholder="00-001, 00-002" testId="sp-pc-input" />
            <FieldInput label="Region (opcjonalny)" value={form.region} onChange={(v) => setForm({ ...form, region: v })} testId="sp-region-input" />
            <label className="flex items-center justify-between p-3 rounded-xl bg-black/[0.03]">
              <span className="text-sm">Aktywna w głosowaniu</span>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            </label>
          </div>
          <DialogFooter className="gap-2 mt-5">
            <Button onClick={onClose} className="btn-soft h-10 px-4 gap-2 text-xs font-medium">
              <X className="w-3.5 h-3.5" /> Anuluj
            </Button>
            <Button onClick={onSave} disabled={saving} data-testid="sp-save-btn" className="btn-brand h-10 px-4 gap-2 text-xs font-semibold">
              <Save className="w-3.5 h-3.5" /> {saving ? "Zapisuję…" : "Zapisz"}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CampaignDialog({ state, onClose, onSaved }) {
  const editing = state.editing;
  const [form, setForm] = useState({
    title: "", subtitle: "", description: "",
    option_label: "miejsce", results_title: "Ranking",
    display_mode: "combobox",
    ends_at: "", is_active: true,
    vote_limit_mode: "days", vote_limit_days: 1,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (state.open) {
      const legacyMode = editing?.vote_limit_mode
        || (editing?.vote_limit_enabled === false ? "off" : "days");
      setForm({
        title: editing?.title || "",
        subtitle: editing?.subtitle || "",
        description: editing?.description || "",
        option_label: editing?.option_label || "miejsce",
        results_title: editing?.results_title || "Ranking",
        display_mode: editing?.display_mode === "radio" ? "radio" : "combobox",
        ends_at: editing?.ends_at ? editing.ends_at.slice(0, 16) : "",
        is_active: editing?.is_active ?? true,
        vote_limit_mode: legacyMode,
        vote_limit_days: Number(editing?.vote_limit_days) || 1,
      });
    }
  }, [state.open, editing]);

  const onSave = async () => {
    if (!form.title.trim()) {
      toast.error("Tytuł jest wymagany");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        subtitle: form.subtitle.trim() || null,
        description: form.description.trim() || null,
        option_label: form.option_label.trim() || "miejsce",
        results_title: form.results_title.trim() || "Ranking",
        display_mode: form.display_mode === "radio" ? "radio" : "combobox",
        ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
        is_active: form.is_active,
        vote_limit_mode: form.vote_limit_mode,
        vote_limit_days: Math.max(1, Math.floor(Number(form.vote_limit_days) || 1)),
        vote_limit_enabled: form.vote_limit_mode !== "off",
      };
      if (editing) {
        await api.patch(`/campaigns/${editing.id}`, payload);
      } else {
        await api.post("/campaigns", payload);
      }
      toast.success("Zapisano");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={state.open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="surface-card border-0 text-black max-w-lg p-0 overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl uppercase tracking-tight">
              {editing ? "Edytuj kampanię" : "Nowa kampania"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            <FieldInput label="Tytuł" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testId="camp-title-input" />
            <FieldInput label="Podtytuł" value={form.subtitle} onChange={(v) => setForm({ ...form, subtitle: v })} testId="camp-subtitle-input" />
            <div>
              <label className="block text-[11px] font-medium tracking-wide uppercase text-black/55 mb-2">
                Opis
              </label>
              <Textarea
                data-testid="camp-desc-input"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="bg-white border-black/10 text-black input-rounded focus-visible:ring-1 focus-visible:ring-brand-green focus-visible:border-brand-green min-h-[100px]"
              />
            </div>
            <FieldInput
              label='Co wybiera użytkownik? (np. „miasto / skatepark", „kraj")'
              value={form.option_label}
              onChange={(v) => setForm({ ...form, option_label: v })}
              testId="camp-option-label-input"
            />
            <FieldInput
              label='Tytuł rankingu (np. „Top skateparki", „Top kraje")'
              value={form.results_title}
              onChange={(v) => setForm({ ...form, results_title: v })}
              testId="camp-results-title-input"
            />

            {/* Display mode */}
            <div>
              <label className="block text-[11px] font-medium tracking-wide uppercase text-black/55 mb-2">
                Sposób wyboru opcji
              </label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: "combobox", label: "Dropdown z autouzupełnianiem", hint: "Dobre przy wielu opcjach" },
                  { id: "radio", label: "Lista radio buttons", hint: "Dobre przy ≤ 10 opcjach" },
                ].map((opt) => {
                  const active = form.display_mode === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      data-testid={`camp-display-mode-${opt.id}`}
                      onClick={() => setForm({ ...form, display_mode: opt.id })}
                      className={`text-left p-3 rounded-xl border transition-all ${
                        active ? "border-brand-green bg-brand-green/8" : "border-black/10 bg-white hover:border-brand-green/40"
                      }`}
                    >
                      <p className={`text-sm font-semibold ${active ? "text-brand-green" : "text-black"}`}>
                        {opt.label}
                      </p>
                      <p className="text-[10px] text-black/45 mt-0.5">{opt.hint}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Vote limit */}
            <div>
              <label className="block text-[11px] font-medium tracking-wide uppercase text-black/55 mb-2">
                Częstotliwość głosowania
              </label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: "off", label: "Bez limitu" },
                  { id: "days", label: "Co X dni" },
                  { id: "lifetime", label: "Raz na zawsze" },
                ].map((opt) => {
                  const active = form.vote_limit_mode === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      data-testid={`camp-limit-mode-${opt.id}`}
                      onClick={() => setForm({ ...form, vote_limit_mode: opt.id })}
                      className={`text-center p-2.5 rounded-xl border transition-all text-xs font-medium ${
                        active ? "border-brand-green bg-brand-green/8 text-brand-green" : "border-black/10 bg-white text-black/70 hover:border-brand-green/40"
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              {form.vote_limit_mode === "days" && (
                <div className="flex items-center gap-2 mt-3">
                  <span className="text-xs text-black/55">Co</span>
                  <Input
                    data-testid="camp-limit-days-input"
                    type="number"
                    min="1"
                    value={form.vote_limit_days}
                    onChange={(e) => setForm({ ...form, vote_limit_days: e.target.value })}
                    className="bg-white border-black/10 text-black input-rounded focus-visible:ring-1 focus-visible:ring-brand-green focus-visible:border-brand-green h-10 w-20 text-center"
                  />
                  <span className="text-xs text-black/55">
                    {Number(form.vote_limit_days) === 1 ? "dzień" : "dni"} na 1 głos
                  </span>
                </div>
              )}
              {form.vote_limit_mode === "lifetime" && (
                <p className="mt-2 text-[11px] text-black/50">
                  Każdy może oddać tylko <strong>1 głos</strong> w całym konkursie (IP + fingerprint).
                </p>
              )}
              {form.vote_limit_mode === "off" && (
                <p className="mt-2 text-[11px] text-black/50">
                  Bez ograniczeń — głosować można dowolną ilość razy.
                </p>
              )}
            </div>

            <div>
              <label className="block text-[11px] font-medium tracking-wide uppercase text-black/55 mb-2">
                Koniec głosowania
              </label>
              <Input
                data-testid="camp-ends-at-input"
                type="datetime-local"
                value={form.ends_at}
                onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
                className="bg-white border-black/10 text-black input-rounded focus-visible:ring-1 focus-visible:ring-brand-green focus-visible:border-brand-green h-11"
              />
            </div>
            <label className="flex items-center justify-between p-3 rounded-xl bg-black/[0.03]">
              <span className="text-sm">
                Kampania aktywna
                <span className="block text-[10px] text-black/45 mt-0.5">
                  Aktywacja zatrzyma inne kampanie
                </span>
              </span>
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
            </label>
          </div>
          <DialogFooter className="gap-2 mt-5">
            <Button onClick={onClose} className="btn-soft h-10 px-4 gap-2 text-xs font-medium">
              <X className="w-3.5 h-3.5" /> Anuluj
            </Button>
            <Button onClick={onSave} disabled={saving} data-testid="camp-save-btn" className="btn-brand h-10 px-4 gap-2 text-xs font-semibold">
              <Save className="w-3.5 h-3.5" /> {saving ? "Zapisuję…" : "Zapisz"}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CopyOptionsDialog({ open, campaigns, destCampaignId, onClose, onCopied }) {
  const [sourceId, setSourceId] = useState("");
  const [copying, setCopying] = useState(false);
  const sources = campaigns.filter((c) => c.id !== destCampaignId);

  useEffect(() => {
    if (open) setSourceId(sources[0]?.id || "");
  }, [open]); // eslint-disable-line

  const onCopy = async () => {
    if (!sourceId || !destCampaignId) return;
    setCopying(true);
    try {
      const { data } = await api.post(`/campaigns/${destCampaignId}/copy-options`, { source_id: sourceId });
      toast.success(`Skopiowano ${data.copied} opcji`);
      onCopied?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCopying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="surface-card border-0 text-black max-w-md p-6">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase tracking-tight">
            Kopiuj opcje
          </DialogTitle>
        </DialogHeader>
        <div className="mt-2 space-y-3">
          <p className="text-sm text-black/60">
            Skopiuj wszystkie opcje głosowania z wybranej kampanii do bieżącej. Istniejące opcje
            zostaną zachowane.
          </p>
          <div>
            <label className="block text-[11px] font-medium tracking-wide uppercase text-black/55 mb-2">
              Skopiuj z kampanii
            </label>
            <Select value={sourceId} onValueChange={setSourceId}>
              <SelectTrigger
                data-testid="copy-source-select"
                className="h-11 bg-white border-black/10 input-rounded"
              >
                <SelectValue placeholder="Wybierz kampanię źródłową" />
              </SelectTrigger>
              <SelectContent>
                {sources.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {sources.length === 0 && (
            <div className="flex items-center gap-2 p-3 rounded-xl bg-amber-50 text-amber-800 text-xs">
              <AlertCircle className="w-4 h-4 shrink-0" /> Brak innych kampanii do kopiowania.
            </div>
          )}
        </div>
        <DialogFooter className="gap-2 mt-5">
          <Button onClick={onClose} className="btn-soft h-10 px-4 gap-2 text-xs font-medium">
            <X className="w-3.5 h-3.5" /> Anuluj
          </Button>
          <Button
            onClick={onCopy}
            disabled={!sourceId || copying}
            data-testid="copy-confirm-btn"
            className="btn-brand h-10 px-4 gap-2 text-xs font-semibold"
          >
            <CopyIcon className="w-3.5 h-3.5" /> {copying ? "Kopiuję…" : "Kopiuj opcje"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldInput({ label, value, onChange, placeholder, testId }) {
  return (
    <div>
      <label className="block text-[11px] font-medium tracking-wide uppercase text-black/55 mb-2">
        {label}
      </label>
      <Input
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="bg-white border-black/10 text-black input-rounded focus-visible:ring-1 focus-visible:ring-brand-green focus-visible:border-brand-green h-11"
      />
    </div>
  );
}

function CampaignWizard({ open, campaigns, onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [title, setTitle] = useState("");
  const [sourceId, setSourceId] = useState("");
  const [displayMode, setDisplayMode] = useState("combobox");
  const [limitMode, setLimitMode] = useState("days");
  const [limitDays, setLimitDays] = useState(1);
  const [activate, setActivate] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(1);
      setTitle("");
      setSourceId(campaigns[0]?.id || "");
      setDisplayMode("combobox");
      setLimitMode("days");
      setLimitDays(1);
      setActivate(true);
    }
  }, [open, campaigns]);

  const finish = async () => {
    if (!title.trim()) {
      toast.error("Podaj tytuł kampanii");
      setStep(1);
      return;
    }
    setBusy(true);
    try {
      const { data: created } = await api.post("/campaigns", {
        title: title.trim(),
        option_label: "miasto / skatepark",
        results_title: "Top skateparki",
        display_mode: displayMode,
        vote_limit_mode: limitMode,
        vote_limit_days: Math.max(1, Math.floor(Number(limitDays) || 1)),
        vote_limit_enabled: limitMode !== "off",
        is_active: activate,
      });
      if (sourceId) {
        try {
          const { data: copy } = await api.post(`/campaigns/${created.id}/copy-options`, {
            source_id: sourceId,
          });
          toast.success(`Stworzono kampanię + skopiowano ${copy.copied} opcji`);
        } catch (e) {
          toast.error("Kampania stworzona, ale kopiowanie opcji nie powiodło się");
        }
      } else {
        toast.success("Kampania stworzona");
      }
      onCreated?.(created.id);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="surface-card border-0 text-black max-w-lg p-6">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase tracking-tight flex items-center gap-2">
            <Wand2 className="w-5 h-5 brand-green" /> Kreator kampanii
          </DialogTitle>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mt-3 mb-4">
          {[1, 2, 3].map((n) => (
            <div
              key={n}
              className={`flex-1 h-1 rounded-full transition-colors ${step >= n ? "bg-brand-green" : "bg-black/10"}`}
            />
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-3">
            <p className="text-xs text-black/55">Krok 1 z 3 · Nazwa kampanii</p>
            <FieldInput
              label="Tytuł kampanii"
              value={title}
              onChange={setTitle}
              placeholder="np. SCOOTIVE ON TOUR — JESIEŃ 2026"
              testId="wizard-title-input"
            />
            <p className="text-[11px] text-black/45">
              Pozostałe opcje (etykieta, ranking) ustawisz po stworzeniu — kreator używa wartości
              domyślnych.
            </p>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <p className="text-xs text-black/55">Krok 2 z 3 · Skopiuj opcje (opcjonalne)</p>
            <div>
              <label className="block text-[11px] font-medium tracking-wide uppercase text-black/55 mb-2">
                Skopiuj listę opcji z innej kampanii
              </label>
              <Select value={sourceId || "__none"} onValueChange={(v) => setSourceId(v === "__none" ? "" : v)}>
                <SelectTrigger
                  data-testid="wizard-source-select"
                  className="h-11 bg-white border-black/10 input-rounded"
                >
                  <SelectValue placeholder="Bez kopiowania — pusta lista" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">— Bez kopiowania (zaczynam od zera) —</SelectItem>
                  {campaigns.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-black/45 mt-2">
                Skopiuje wszystkie miasta/skateparki ze wskazanej kampanii do nowej.
              </p>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <p className="text-xs text-black/55">Krok 3 z 3 · Ustawienia głosowania</p>
            <div>
              <label className="block text-[11px] font-medium tracking-wide uppercase text-black/55 mb-2">
                Sposób wyboru
              </label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: "combobox", label: "Dropdown" },
                  { id: "radio", label: "Radio buttons" },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    data-testid={`wizard-display-${opt.id}`}
                    onClick={() => setDisplayMode(opt.id)}
                    className={`p-2.5 rounded-xl border text-sm font-medium ${
                      displayMode === opt.id
                        ? "border-brand-green bg-brand-green/8 text-brand-green"
                        : "border-black/10 bg-white text-black/70"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-medium tracking-wide uppercase text-black/55 mb-2">
                Limit głosowania
              </label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: "off", label: "Bez limitu" },
                  { id: "days", label: "Co X dni" },
                  { id: "lifetime", label: "Raz na zawsze" },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    data-testid={`wizard-limit-${opt.id}`}
                    onClick={() => setLimitMode(opt.id)}
                    className={`p-2.5 rounded-xl border text-xs font-medium ${
                      limitMode === opt.id
                        ? "border-brand-green bg-brand-green/8 text-brand-green"
                        : "border-black/10 bg-white text-black/70"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {limitMode === "days" && (
                <div className="flex items-center gap-2 mt-3">
                  <span className="text-xs text-black/55">Co</span>
                  <Input
                    data-testid="wizard-limit-days"
                    type="number"
                    min="1"
                    value={limitDays}
                    onChange={(e) => setLimitDays(e.target.value)}
                    className="bg-white border-black/10 text-black input-rounded h-10 w-20 text-center"
                  />
                  <span className="text-xs text-black/55">
                    {Number(limitDays) === 1 ? "dzień" : "dni"} na 1 głos
                  </span>
                </div>
              )}
            </div>
            <label className="flex items-center justify-between p-3 rounded-xl bg-black/[0.03]">
              <span className="text-sm">
                Aktywuj od razu
                <span className="block text-[10px] text-black/45 mt-0.5">
                  Zatrzyma inne aktywne kampanie
                </span>
              </span>
              <Switch
                data-testid="wizard-activate-switch"
                checked={activate}
                onCheckedChange={setActivate}
              />
            </label>
          </div>
        )}

        <DialogFooter className="gap-2 mt-5">
          <Button onClick={onClose} className="btn-soft h-10 px-4 gap-2 text-xs font-medium">
            <X className="w-3.5 h-3.5" /> Anuluj
          </Button>
          {step > 1 && (
            <Button
              onClick={() => setStep(step - 1)}
              data-testid="wizard-back-btn"
              className="btn-soft h-10 px-4 gap-2 text-xs font-medium"
            >
              Wstecz
            </Button>
          )}
          {step < 3 && (
            <Button
              onClick={() => {
                if (step === 1 && !title.trim()) {
                  toast.error("Podaj tytuł kampanii");
                  return;
                }
                setStep(step + 1);
              }}
              data-testid="wizard-next-btn"
              className="btn-brand h-10 px-4 gap-2 text-xs font-semibold"
            >
              Dalej <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          )}
          {step === 3 && (
            <Button
              onClick={finish}
              disabled={busy}
              data-testid="wizard-finish-btn"
              className="btn-brand h-10 px-4 gap-2 text-xs font-semibold"
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> {busy ? "Tworzę…" : "Stwórz kampanię"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ImportCsvDialog({ open, campaignId, onClose, onImported }) {
  const [csv, setCsv] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const EXAMPLE = `miasto;skatepark;region;kody_pocztowe
Białystok;Skatepark Zwierzyniecki;podlaskie;15-001|15-002
Warszawa;AvePark;mazowieckie;00-001
Łódź;Skatepark Lublinek;łódzkie;90-001|90-002`;

  const submit = async () => {
    if (!campaignId) {
      toast.error("Brak wybranej kampanii");
      return;
    }
    if (!csv.trim()) {
      toast.error("Wklej najpierw zawartość CSV");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post(`/campaigns/${campaignId}/import-options`, csv, {
        headers: { "Content-Type": "text/csv" },
        transformRequest: [(d) => d],
      });
      setResult(r.data);
      toast.success(`Zaimportowano ${r.data.inserted} opcji`);
      onImported?.();
    } catch (e) {
      const detail = e.response?.data;
      setResult(detail || { inserted: 0, errors: [{ line: 0, message: formatApiError(e) }] });
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          setCsv("");
          setResult(null);
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import opcji z CSV</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-black/55 leading-relaxed">
            Wklej dane CSV (separator <code className="bg-black/5 px-1 rounded">;</code> lub{" "}
            <code className="bg-black/5 px-1 rounded">,</code>). Wymagane:{" "}
            <strong>miasto</strong> (lub <code>city</code>). Opcjonalnie:{" "}
            <strong>skatepark</strong>, <strong>region</strong>,{" "}
            <strong>kody_pocztowe</strong> (separator <code>|</code> lub spacja).
          </p>
          <pre
            className="text-[10px] bg-black/5 p-2 rounded font-mono overflow-x-auto cursor-pointer"
            onClick={() => setCsv(EXAMPLE)}
            title="Kliknij, aby wkleić przykład"
          >
{EXAMPLE}
          </pre>
          <Textarea
            value={csv}
            onChange={(e) => setCsv(e.target.value)}
            placeholder="Wklej CSV tutaj…"
            rows={10}
            data-testid="import-csv-textarea"
            className="font-mono text-xs"
          />
          {result && (
            <div
              className={`text-xs p-2.5 rounded-lg ${
                result.inserted > 0 ? "bg-emerald-50 text-emerald-900" : "bg-amber-50 text-amber-900"
              }`}
            >
              <p className="font-semibold">
                Wstawiono: {result.inserted ?? 0} · Pominięto: {result.skipped ?? 0}
              </p>
              {Array.isArray(result.errors) && result.errors.length > 0 && (
                <ul className="mt-1 list-disc pl-4">
                  {result.errors.slice(0, 6).map((er, i) => (
                    <li key={i}>
                      linia {er.line}: {er.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={() => onClose()} className="btn-soft h-9 px-3 text-xs">
            Zamknij
          </Button>
          <Button
            onClick={submit}
            disabled={busy}
            data-testid="import-csv-submit"
            className="btn-brand h-9 px-4 text-xs gap-2"
          >
            {busy ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            Importuj
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ExportDialog({ open, onClose, campaigns, defaultCampaignId, onExport }) {
  const [campaignId, setCampaignId] = useState(defaultCampaignId || "");
  const [scope, setScope] = useState("votes"); // votes | ranking
  const [format, setFormat] = useState("csv"); // csv | json
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setCampaignId(defaultCampaignId || "");
      setScope("votes");
      setFormat("csv");
      setFrom("");
      setTo("");
    }
  }, [open, defaultCampaignId]);

  const run = async () => {
    setBusy(true);
    await onExport({
      campaign_id: campaignId,
      scope,
      format,
      from: from ? new Date(from).toISOString() : undefined,
      to: to ? new Date(to).toISOString() : undefined,
    });
    setBusy(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="surface-card border-0 text-black max-w-md p-6">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase tracking-tight flex items-center gap-2">
            <Download className="w-5 h-5 brand-green" /> Eksport danych
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-3">
          <div>
            <label className="block text-[11px] font-medium uppercase tracking-wider text-black/55 mb-2">
              Kampania
            </label>
            <Select value={campaignId} onValueChange={setCampaignId}>
              <SelectTrigger
                data-testid="export-campaign-select"
                className="h-11 bg-white border-black/10 input-rounded"
              >
                <SelectValue placeholder="Wybierz kampanię" />
              </SelectTrigger>
              <SelectContent>
                {campaigns.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-[11px] font-medium uppercase tracking-wider text-black/55 mb-2">
              Co eksportujemy?
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                data-testid="export-scope-votes"
                onClick={() => setScope("votes")}
                className={`p-3 rounded-xl border text-left transition-all ${
                  scope === "votes" ? "border-brand-green bg-brand-green/8" : "border-black/10 bg-white"
                }`}
              >
                <p className="text-sm font-semibold">Wszystkie głosy</p>
                <p className="text-[11px] text-black/50 mt-0.5">Lista pojedynczych głosów</p>
              </button>
              <button
                type="button"
                data-testid="export-scope-ranking"
                onClick={() => setScope("ranking")}
                className={`p-3 rounded-xl border text-left transition-all ${
                  scope === "ranking" ? "border-brand-green bg-brand-green/8" : "border-black/10 bg-white"
                }`}
              >
                <p className="text-sm font-semibold">Ranking</p>
                <p className="text-[11px] text-black/50 mt-0.5">Podsumowanie z procentami</p>
              </button>
            </div>
          </div>

          {scope === "votes" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-medium uppercase tracking-wider text-black/55 mb-2">
                  Od (opcjonalne)
                </label>
                <Input
                  type="datetime-local"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  data-testid="export-from"
                  className="bg-white border-black/10 input-rounded h-11"
                />
              </div>
              <div>
                <label className="block text-[11px] font-medium uppercase tracking-wider text-black/55 mb-2">
                  Do (opcjonalne)
                </label>
                <Input
                  type="datetime-local"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  data-testid="export-to"
                  className="bg-white border-black/10 input-rounded h-11"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-[11px] font-medium uppercase tracking-wider text-black/55 mb-2">
              Format
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="export-format-csv"
                onClick={() => setFormat("csv")}
                className={`flex-1 p-2.5 rounded-xl border text-xs font-medium ${
                  format === "csv" ? "border-brand-green bg-brand-green/8 text-brand-green" : "border-black/10 bg-white"
                }`}
              >
                CSV (Excel-PL)
              </button>
              <button
                type="button"
                data-testid="export-format-json"
                onClick={() => setFormat("json")}
                className={`flex-1 p-2.5 rounded-xl border text-xs font-medium ${
                  format === "json" ? "border-brand-green bg-brand-green/8 text-brand-green" : "border-black/10 bg-white"
                }`}
              >
                JSON
              </button>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 mt-5">
          <Button onClick={onClose} className="btn-soft h-10 px-4 gap-2 text-xs font-medium">
            <X className="w-3.5 h-3.5" /> Anuluj
          </Button>
          <Button
            onClick={run}
            disabled={busy || !campaignId}
            data-testid="export-confirm-btn"
            className="btn-brand h-10 px-4 gap-2 text-xs font-semibold"
          >
            <Download className="w-3.5 h-3.5" /> {busy ? "Eksportuję…" : "Pobierz plik"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
